import React from 'react'
import { useTranslations } from 'next-intl'
import { MapRoute } from './features/course/map-preview'
import { cn } from '@onclicks/sharedcomponents/ui'
import {
  CourseEstimateResult,
  TypeVehicule,
  TypeVehiculeLabel,
  formatPrice,
} from '../types/course-estimate'
import { Adresse } from '../types/models'
import Icon from './shared/ui/icon'

const typeToIcon = (type: string) => {
  switch (type) {
    case 'start':
      return 'my_location'
    case 'end':
      return 'flag'
    default:
      return 'fiber_manual_record'
  }
}

const Itineraire = ({
  itinerarySteps,
  estimate,
  estimateLoading,
  estimateError,
  isEditing = false,
  feesTotal = 0,
  discountsTotal = 0,
  serviceName = null,
}: {
  itinerarySteps: {
    type: string
    label: string
    name: string
    adresse: Adresse
  }[]
  estimate?: CourseEstimateResult | null
  estimateLoading?: boolean
  estimateError?: string | null
  // Édition d'une course existante : affiche en plus les frais, les ristournes
  // et le total (prix + frais − ristournes) à côté du prix de la course.
  isEditing?: boolean
  feesTotal?: number
  discountsTotal?: number
  // Service choisi par l'utilisateur, affiché à la place du seul type de véhicule.
  serviceName?: string | null
}) => {
  const t = useTranslations('estimate')
  const tReservation = useTranslations('reservation_detail')
  const hasSteps = itinerarySteps.length > 0

  return (
    <div className="flex flex-col gap-4">
      {hasSteps && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-0">
            {itinerarySteps.map((step, index) => {
              const isLast = index === itinerarySteps.length - 1

              return (
                <div key={index} className="flex gap-4">
                  <div className="flex flex-col items-center w-6">
                    <Icon
                      name={typeToIcon(step.type)}
                      className={cn(
                        'text-xl',
                        step.type === 'step' ? 'text-slate-400' : 'text-primary',
                      )}
                    />

                    {!isLast && (
                      <div className="w-0.5 h-full my-1 border-l-2 border-dashed border-slate-300 dark:border-slate-700" />
                    )}
                  </div>

                  <div className={cn('pb-6', isLast && 'pb-0')}>
                    <p className="font-semibold text-sm md:text-base leading-tight">{step.name}</p>
                    <p className="text-base! uppercase tracking-widest font-bold text-slate-400 dark:text-white">
                      {step.label}
                    </p>
                  </div>
                </div>
              )
            })}
          </div>

          <div className="rounded-xl overflow-hidden h-64 md:h-full min-h-50 border border-slate-200 dark:border-slate-700 relative">
            {itinerarySteps.length >= 2 && (
              <MapRoute adresses={itinerarySteps.map((s) => s.adresse)} />
            )}
          </div>
        </div>
      )}

      {(estimate || estimateLoading || estimateError) && (
        <div className="rounded-xl border border-primary/20 bg-primary/5 dark:bg-white/10 dark:border-white p-4 md:p-5">
          {estimateError && <p className="text-sm text-red-500">{estimateError}</p>}

          {estimateLoading && !estimate && (
            <p className="text-sm text-slate-500 dark:text-slate-400">{t('calculating')}</p>
          )}

          {estimate && (
            <div className="flex flex-col gap-3">
              <div className="grid grid-cols-2 gap-y-4">
                <div>
                  <p className="text-[10px]! font-bold uppercase tracking-widest text-primary dark:text-white">
                    {t('estimated_price')}
                  </p>
                  <p
                    className={`${isEditing ? 'text-lg' : 'text-2xl! md:text-3xl!'} font-bold text-slate-900 dark:text-white`}
                  >
                    {formatPrice(estimate.prixTotal)}
                  </p>
                  {/* Ventilation : le TVAC reste le montant annoncé, le hors-taxe en découle. */}
                  <p className="text-xs! text-slate-500 dark:text-white/70">
                    {formatPrice(estimate.prixTotalHtva)} HTVA +{' '}
                    {formatPrice(estimate.prixTotalTva)} TVA ({(estimate.tauxTva * 100).toFixed(0)}{' '}
                    %)
                  </p>
                </div>
                {isEditing && (
                  <div>
                    <p className="text-[10px]! font-bold uppercase tracking-widest text-primary dark:text-white">
                      {tReservation('section_fees')}
                    </p>
                    <p className="text-lg font-bold text-slate-900 dark:text-white">
                      {formatPrice(feesTotal)}
                    </p>
                  </div>
                )}

                {isEditing && discountsTotal > 0 && (
                  <div>
                    <p className="text-[10px]! font-bold uppercase tracking-widest text-primary dark:text-white">
                      {tReservation('section_discounts')}
                    </p>
                    <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                      -{formatPrice(discountsTotal)}
                    </p>
                  </div>
                )}

                {isEditing && (
                  <div>
                    <p className="text-[10px]! font-bold uppercase tracking-widest text-primary dark:text-white">
                      {tReservation('final_total_label')}
                    </p>
                    <p className="text-3xl font-bold text-slate-900 dark:text-white">
                      {formatPrice(Math.max(0, estimate.prixTotal + feesTotal - discountsTotal))}
                    </p>
                  </div>
                )}

                <div>
                  <p className="text-[10px]! font-bold uppercase tracking-widest text-slate-400 dark:text-white">
                    {t('service')}
                  </p>
                  <p className="text-sm! font-semibold text-slate-700 dark:text-white">
                    {serviceName
                      ? estimate.typeVehicule === TypeVehicule.Van
                        ? `${TypeVehiculeLabel[TypeVehicule.Van]} - ${serviceName}`
                        : serviceName
                      : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-xs! text-slate-400 uppercase tracking-wider dark:text-white">
                    {t('distance')}
                  </p>
                  <p className="text-xs! text-slate-700 dark:text-white font-medium">
                    {estimate.distanceKm.toLocaleString('fr-BE')} km
                  </p>
                </div>
                <div>
                  <p className="text-xs! text-slate-400 dark:text-white uppercase tracking-wider">
                    {t('base_price')}
                  </p>
                  <p className="text-xs! text-slate-700 dark:text-white font-medium">
                    {formatPrice(estimate.prixBase)}
                  </p>
                </div>
                {estimate.heurePointe && (
                  <div>
                    <p className="text-xs! text-slate-400 dark:text-white uppercase tracking-wider">
                      {t('peak_hour')}
                    </p>
                    <p className="text-xs! text-slate-700 dark:text-white font-medium">
                      +{(estimate.majorationHeurePointe * 100).toFixed(0)}%
                    </p>
                  </div>
                )}
                {estimate.horsBruxelles && (
                  <div>
                    <p className="text-xs! text-slate-400 dark:text-white uppercase tracking-wider">
                      {t('zone')}
                    </p>
                    <p className="text-xs! text-slate-700 dark:text-white font-medium">
                      {t('outside_brussels')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default Itineraire
